import { useState, useEffect, useRef, type ChangeEvent } from 'react';
import { parse as parseYaml } from 'yaml';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Switch } from '../ui/switch';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle
} from '../ui/dialog';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../ui/collapsible';
import { Search, Plus, Edit, Trash2, Eye, Globe, AlertCircle, Loader2, Download, Shield, Zap, Upload, ChevronDown } from 'lucide-react';

/**
 * Parse a tool schema pasted/uploaded as JSON or YAML into a plain object.
 * JSON is attempted first (fast path + precise errors); YAML is the fallback
 * (YAML is a superset of JSON, so this also covers `.yaml`/`.yml` specs).
 * @throws Error when the text is empty or not a JSON/YAML object.
 */
function parseSchemaText(text: string): Record<string, unknown> {
  const trimmed = text.trim();
  if (!trimmed) {
    throw new Error('Schema is empty');
  }
  let parsed: unknown;
  try {
    parsed = JSON.parse(trimmed);
  } catch {
    parsed = parseYaml(trimmed);
  }
  if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
    throw new Error('Schema must be a JSON or YAML object');
  }
  return parsed as Record<string, unknown>;
}

/**
 * Derive a base URL from an OpenAPI/Swagger schema so the admin does not have to
 * retype it. Supports OpenAPI 3.x (`servers[0].url`) and Swagger 2.x
 * (`schemes[0]://host + basePath`). Returns undefined when nothing usable is found.
 */
function extractBaseUrlFromSchema(schema: Record<string, unknown>): string | undefined {
  // OpenAPI 3.x
  const servers = schema.servers;
  if (Array.isArray(servers) && servers.length > 0) {
    const first = servers[0] as { url?: unknown };
    if (typeof first.url === 'string' && first.url.trim() && !first.url.includes('{')) {
      return first.url.trim().replace(/\/+$/, '');
    }
  }
  // Swagger 2.x
  const host = schema.host;
  if (typeof host === 'string' && host.trim()) {
    const schemes = schema.schemes;
    const scheme = Array.isArray(schemes) && typeof schemes[0] === 'string' ? schemes[0] : 'https';
    const basePath = typeof schema.basePath === 'string' ? schema.basePath : '';
    return `${scheme}://${host.trim()}${basePath}`.replace(/\/+$/, '');
  }
  return undefined;
}
import { 
  apiService, 
  ToolDefinition, 
  CreateToolDefinitionRequest, 
  UpdateToolDefinitionRequest, 
  ToolCategory,
  ToolAuthConfig,
  CreateToolAuthConfigRequest,
  AuthType,
  Customer,
  AuthTypeInfo
} from '../../services/api';

export function ToolDefinitionsPage() {
  const [tools, setTools] = useState<ToolDefinition[]>([]);
  const [categories, setCategories] = useState<ToolCategory[]>([]);
  const [authConfigs, setAuthConfigs] = useState<ToolAuthConfig[]>([]);
  const [, setCustomers] = useState<Customer[]>([]);
  const [authTypes, setAuthTypes] = useState<AuthTypeInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | { message?: string } | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [viewingTool, setViewingTool] = useState<ToolDefinition | null>(null);
  const [editingTool, setEditingTool] = useState<ToolDefinition | null>(null);
  const [saving, setSaving] = useState(false);
  const [schemaUrl, setSchemaUrl] = useState('');
  const [loadingSchema, setLoadingSchema] = useState(false);
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const schemaFileInputRef = useRef<HTMLInputElement>(null);
  const [tagsInput, setTagsInput] = useState('');
  const [showAuthSection, setShowAuthSection] = useState(false);
  const [authFormData, setAuthFormData] = useState<CreateToolAuthConfigRequest>({
    toolDefinitionId: '',
    authType: AuthType.HEADER,
    headerName: '',
    headerValue: '',
    description: '',
    isActive: true
  });
  const [savingAuth, setSavingAuth] = useState(false);
  const [formData, setFormData] = useState<CreateToolDefinitionRequest>({
    name: '',
    displayName: '',
    description: '',
    category: 'function',
    version: '1.0.0',
    schema: {},
    configuration: {},
    executionConfig: {},
    tags: [],
    author: '',
    documentation: '',
    iconUrl: '',
    baseUrl: '',
    isPublic: false,
    fillerEnabled: true,
    detectDynamicBodyFields: false,
    authStrategy: 'admin'
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Always load core data
      const [toolsData, categoriesData] = await Promise.all([
        apiService.getToolDefinitions(),
        apiService.getToolCategories()
      ]);
      setTools(toolsData);
      setCategories(categoriesData.categories);
      
      // Load authentication data independently - each call can fail without blocking the others
      try {
        const authConfigsData = await apiService.getToolAuthConfigs({ limit: 100 });
        setAuthConfigs(authConfigsData.configs);
      } catch (e) { console.warn('Failed to load auth configs:', (e as Error).message); }

      try {
        const customersData = await apiService.getCustomers({ limit: 100 });
        setCustomers(customersData.customers);
      } catch (e) { console.warn('Failed to load customers:', (e as Error).message); }

      try {
        const authTypesData = await apiService.getAuthTypes();
        setAuthTypes(authTypesData.authTypes);
      } catch (e) { console.warn('Failed to load auth types:', (e as Error).message); }
      
    } catch (err: any) {
      setError(err.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const filteredTools = tools.filter(tool => {
    const matchesSearch = (tool.displayName?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
                         (tool.name?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
                         (tool.description?.toLowerCase() || '').includes(searchTerm.toLowerCase());
    const matchesCategory = !selectedCategory || selectedCategory === 'all' || tool.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const handleView = (tool: ToolDefinition) => {
    setViewingTool(tool);
    setIsDialogOpen(true);
  };

  const handleEdit = (tool: ToolDefinition) => {
    setEditingTool(tool);
    setFormData({
      name: tool.name,
      displayName: tool.displayName,
      description: tool.description,
      category: tool.category,
      version: tool.version,
      schema: tool.schema,
      configuration: tool.configuration,
      executionConfig: tool.executionConfig,
      tags: Array.isArray(tool.tags) ? tool.tags.filter(tag => tag && typeof tag === 'string') : [],
      author: tool.author || '',
      documentation: tool.documentation || '',
      iconUrl: tool.iconUrl || '',
      baseUrl: tool.baseUrl || '',
      isPublic: tool.isPublic,
      fillerEnabled: tool.fillerEnabled ?? true,
      detectDynamicBodyFields: tool.detectDynamicBodyFields ?? false,
      authStrategy: tool.authStrategy ?? 'admin',
      customerAuthType: tool.customerAuthType || 'bearer',
      customerAuthHeaderName: tool.customerAuthHeaderName || '',
      customerAuthDescription: tool.customerAuthDescription || '',
    });
    setTagsInput(Array.isArray(tool.tags) ? tool.tags.filter(tag => tag && typeof tag === 'string').join(', ') : '');
    setViewingTool(null);
    setShowAuthSection(false);
    setIsDialogOpen(true);
  };

  const handleAddAuthConfig = () => {
    const toolId = viewingTool?.id || editingTool?.id;
    if (!toolId || authTypes.length === 0) {
      console.warn('Authentication not available - endpoints may not be implemented');
      return;
    }
    
    setAuthFormData({
      toolDefinitionId: toolId,
      authType: AuthType.HEADER,
      headerName: '',
      headerValue: '',
      description: '',
      isActive: true
    });
    setShowAuthSection(true);
  };

  const handleSaveAuthConfig = async () => {
    try {
      setSavingAuth(true);
      const newConfig = await apiService.createToolAuthConfig(authFormData);
      setAuthConfigs([...authConfigs, newConfig]);
      setShowAuthSection(false);
      setAuthFormData({
        toolDefinitionId: '',
        authType: AuthType.HEADER,
        headerName: '',
        headerValue: '',
        description: '',
        isActive: true
      });
    } catch (err: any) {
      setError(err.message || 'Failed to save authentication configuration');
    } finally {
      setSavingAuth(false);
    }
  };

  const handleDeleteAuthConfig = async (configId: string) => {
    if (!confirm('Are you sure you want to delete this authentication configuration?')) {
      return;
    }

    try {
      await apiService.deleteToolAuthConfig(configId);
      setAuthConfigs(authConfigs.filter(c => c.id !== configId));
    } catch (err: any) {
      setError(err.message || 'Failed to delete authentication configuration');
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      
      // Process tags from input string before saving
      const tags = tagsInput
        .split(',')
        .map(tag => tag.trim())
        .filter(tag => tag.length > 0);
      const finalFormData = {...formData, tags};
      
      if (editingTool) {
        const updateData: UpdateToolDefinitionRequest = {
          displayName: finalFormData.displayName,
          description: finalFormData.description,
          schema: finalFormData.schema,
          configuration: finalFormData.configuration,
          executionConfig: finalFormData.executionConfig,
          tags: finalFormData.tags,
          documentation: finalFormData.documentation,
          iconUrl: finalFormData.iconUrl,
          baseUrl: finalFormData.baseUrl,
          isPublic: finalFormData.isPublic,
          fillerEnabled: finalFormData.fillerEnabled,
          detectDynamicBodyFields: finalFormData.detectDynamicBodyFields,
          authStrategy: finalFormData.authStrategy,
          customerAuthType: finalFormData.customerAuthType,
          customerAuthHeaderName: finalFormData.customerAuthHeaderName,
          customerAuthDescription: finalFormData.customerAuthDescription,
        };
        
        const updatedTool = await apiService.updateToolDefinition(editingTool.id, updateData);
        setTools(tools.map(t => t.id === editingTool.id ? updatedTool : t));
      } else {
        const newTool = await apiService.createToolDefinition(finalFormData);
        setTools([...tools, newTool]);
      }

      closeDialog();
    } catch (err: any) {
      setError(err.message || 'Failed to save tool');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this tool? This action cannot be undone.')) {
      return;
    }

    try {
      await apiService.deleteToolDefinition(id);
      setTools(tools.filter(t => t.id !== id));
    } catch (err: any) {
      setError(err.message || 'Failed to delete tool');
    }
  };

  const toggleActive = async (tool: ToolDefinition) => {
    try {
      const updateData: UpdateToolDefinitionRequest = {
        status: tool.status === 'active' ? 'inactive' : 'active'
      };
      
      const updatedTool = await apiService.updateToolDefinition(tool.id, updateData);
      setTools(tools.map(t => t.id === tool.id ? updatedTool : t));
    } catch (err: any) {
      setError(err.message || 'Failed to update tool status');
    }
  };

  const closeDialog = () => {
    setIsDialogOpen(false);
    setEditingTool(null);
    setViewingTool(null);
    setSchemaUrl('');
    setLoadingSchema(false);
    setAdvancedOpen(false);
    setTagsInput('');
    setShowAuthSection(false);
    setAuthFormData({
      toolDefinitionId: '',
      authType: AuthType.HEADER,
      headerName: '',
      headerValue: '',
      description: '',
      isActive: true
    });
    setFormData({
      name: '',
      displayName: '',
      description: '',
      category: 'function',
      version: '1.0.0',
      schema: {},
      configuration: {},
      executionConfig: {},
      tags: [],
      author: '',
      documentation: '',
      iconUrl: '',
      baseUrl: '',
      isPublic: false,
      fillerEnabled: true,
      detectDynamicBodyFields: false,
      authStrategy: 'admin'
    });
  };

  const getAuthTypeBadgeColor = (authType: string) => {
    const colors: Record<string, string> = {
      'header': 'bg-orange-100 text-orange-800',
      'bearer': 'bg-teal-100 text-teal-800',
      'api_key': 'bg-indigo-100 text-indigo-800',
      'basic_auth': 'bg-pink-100 text-pink-800',
      'custom': 'bg-gray-100 text-gray-800'
    };
    return colors[authType] || 'bg-gray-100 text-gray-800';
  };

  const getAuthTypeDisplayName = (authType: string) => {
    const names: Record<string, string> = {
      'header': 'Custom Header',
      'bearer': 'Bearer Token',
      'api_key': 'API Key',
      'basic_auth': 'Basic Auth',
      'custom': 'Custom'
    };
    return names[authType] || authType;
  };

  const getToolAuthConfigs = (toolId: string) => {
    return authConfigs.filter(config => config.toolDefinitionId === toolId);
  };

  /**
   * Apply a parsed schema to the form and, when the Base URL field is still
   * empty, auto-fill it from the schema's servers/host so the admin does not
   * have to retype it.
   */
  const applyLoadedSchema = (schema: Record<string, unknown>) => {
    setFormData(prev => {
      const derivedBaseUrl = extractBaseUrlFromSchema(schema);
      return {
        ...prev,
        schema,
        baseUrl: prev.baseUrl?.trim() ? prev.baseUrl : (derivedBaseUrl ?? prev.baseUrl),
      };
    });
  };

  const handleSchemaFileUpload = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const text = await file.text();
      applyLoadedSchema(parseSchemaText(text));
      setError(null);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to parse file';
      setError(`Failed to load schema file: ${errorMessage}`);
    } finally {
      e.target.value = ''; // allow re-uploading the same file
    }
  };

  const loadSchemaFromUrl = async () => {
    if (!schemaUrl.trim()) return;

    setLoadingSchema(true);
    try {
      const response = await fetch(schemaUrl);

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      // Accept JSON or YAML regardless of content-type (many specs are served
      // as text/plain or application/yaml).
      const text = await response.text();
      applyLoadedSchema(parseSchemaText(text));
      setSchemaUrl(''); // Clear the URL after successful load
      setError(null);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load schema from URL';
      setError(`Failed to load schema: ${errorMessage}`);
    } finally {
      setLoadingSchema(false);
    }
  };

  const getCategoryBadgeColor = (category: string) => {
    const colors: Record<string, string> = {
      'function': 'bg-blue-100 text-blue-800',
      'api': 'bg-green-100 text-green-800',
      'webhook': 'bg-purple-100 text-purple-800',
      'mcp_server': 'bg-orange-100 text-orange-800'
    };
    return colors[category] || 'bg-gray-100 text-gray-800';
  };

  const getCategoryDisplayName = (category: string) => {
    const names: Record<string, string> = {
      'function': 'Function',
      'api': 'API',
      'webhook': 'Webhook',
      'mcp_server': 'MCP Server'
    };
    return names[category] || category;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin" />
        <span className="ml-2">Loading tools...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <div className="flex items-center">
            <AlertCircle className="w-5 h-5 text-red-400 mr-2" />
            <span className="text-red-800">{typeof error === 'string' ? error : (error?.message || 'An error occurred')}</span>
            <Button variant="ghost" size="sm" onClick={() => setError(null)} className="ml-auto">
              ×
            </Button>
          </div>
        </div>
      )}

      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Tool Definitions</h1>
          <p className="text-gray-600">Manage available tools and functions</p>
        </div>
        <Button onClick={() => { setViewingTool(null); setEditingTool(null); setIsDialogOpen(true); }}>
          <Plus className="w-4 h-4 mr-2" />
          New Tool
        </Button>
      </div>

      <div className="flex items-center space-x-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
          <Input
            placeholder="Search tools..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        <Select value={selectedCategory} onValueChange={setSelectedCategory}>
          <SelectTrigger className="w-48">
            <SelectValue placeholder="Filter by category" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Categories</SelectItem>
            {categories.map((category) => (
              <SelectItem key={category.id} value={category.id}>
                {category.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredTools.map((tool) => {
          const toolAuthConfigs = getToolAuthConfigs(tool.id);
          const hasActiveAuth = toolAuthConfigs.some(config => config.isActive);
          
          return (
            <Card key={tool.id} className="hover:shadow-md transition-shadow">
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div className="flex items-start space-x-3 flex-1">
                    {tool.iconUrl && (
                      <img src={tool.iconUrl} alt="" className="w-8 h-8 mt-1" />
                    )}
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <CardTitle className="text-lg">{tool.displayName}</CardTitle>
                        {hasActiveAuth && (
                          <Shield className="w-4 h-4 text-green-600" />
                        )}
                      </div>
                      <CardDescription className="mt-1">{tool.description}</CardDescription>
                    </div>
                  </div>
                  <Switch
                    checked={tool.status === 'active'}
                    onCheckedChange={() => toggleActive(tool)}
                  />
                </div>
                <div className="flex items-center justify-between mt-2">
                  <div className="flex items-center space-x-2">
                    <Badge className={getCategoryBadgeColor(tool.category)}>
                      {getCategoryDisplayName(tool.category)}
                    </Badge>
                    {tool.isPublic && (
                      <Badge variant="outline" className="text-green-700 border-green-200">
                        <Globe className="w-3 h-3 mr-1" />
                        Public
                      </Badge>
                    )}
                    {toolAuthConfigs.length > 0 && (
                      <Badge variant="outline" className="text-blue-700 border-blue-200">
                        <Shield className="w-3 h-3 mr-1" />
                        {toolAuthConfigs.length} Auth
                      </Badge>
                    )}
                    {tool.fillerEnabled !== false && (
                      <Badge variant="outline" className="text-yellow-700 border-yellow-200">
                        <Zap className="w-3 h-3 mr-1" />
                        Filler
                      </Badge>
                    )}
                    {tool.authStrategy === 'none' && (
                      <Badge variant="outline" className="text-gray-500 border-gray-200">
                        No Auth
                      </Badge>
                    )}
                    {tool.authStrategy === 'customer' && (
                      <Badge variant="outline" className="text-cyan-700 border-cyan-200">
                        Customer Auth
                      </Badge>
                    )}
                  </div>
                </div>
              </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600">Version: {tool.version}</span>
                  <span className="text-gray-500">{tool.usageCount ?? 0} times used</span>
                </div>
                <div className="flex flex-wrap gap-1">
                  {(tool.tags || []).filter(tag => tag && typeof tag === 'string').slice(0, 3).map((tag) => (
                    <Badge key={tag} variant="secondary" className="text-xs">
                      {tag}
                    </Badge>
                  ))}
                  {(tool.tags || []).filter(tag => tag && typeof tag === 'string').length > 3 && (
                    <Badge variant="secondary" className="text-xs">
                      +{(tool.tags || []).filter(tag => tag && typeof tag === 'string').length - 3}
                    </Badge>
                  )}
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-500">
                    by {tool.author || 'Unknown'}
                  </span>
                  <div className="flex items-center space-x-1">
                    <Button variant="ghost" size="sm" onClick={() => handleView(tool)}>
                      <Eye className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => handleEdit(tool)}>
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={() => handleDelete(tool.id)}
                      className="text-red-600 hover:text-red-700"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
          );
        })}
      </div>

      {filteredTools.length === 0 && !loading && (
        <Card>
          <CardContent className="py-12 text-center">
            <p className="text-gray-500">No tools found.</p>
            <Button className="mt-4" onClick={() => { setViewingTool(null); setEditingTool(null); setIsDialogOpen(true); }}>
              Create first tool
            </Button>
          </CardContent>
        </Card>
      )}

      <Dialog open={isDialogOpen} onOpenChange={closeDialog}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {viewingTool ? 'Tool Details' :
               editingTool ? 'Edit Tool' : 'Create New Tool'}
            </DialogTitle>
            <DialogDescription>
              {viewingTool ? 'Detailed view of the tool definition' :
               'Configure a new tool for the AI platform'}
            </DialogDescription>
          </DialogHeader>

          {viewingTool ? (
            <div className="space-y-6">
              <div className="grid grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div>
                    <h4 className="text-sm font-medium text-gray-700 mb-2">Tool Information</h4>
                    <div className="space-y-2 text-sm">
                      <div><strong>Name:</strong> {viewingTool.name}</div>
                      <div><strong>Display Name:</strong> {viewingTool.displayName}</div>
                      <div><strong>Version:</strong> {viewingTool.version}</div>
                      <div><strong>Author:</strong> {viewingTool.author}</div>
                      <div><strong>Category:</strong> 
                        <Badge className={`ml-2 ${getCategoryBadgeColor(viewingTool.category)}`}>
                          {getCategoryDisplayName(viewingTool.category)}
                        </Badge>
                      </div>
                      <div><strong>Status:</strong> {viewingTool.status === 'active' ? 'Active' : 'Inactive'}</div>
                      <div><strong>Public:</strong> {viewingTool.isPublic ? 'Yes' : 'No'}</div>
                      <div className="flex items-center gap-1">
                        <strong>Filler:</strong>
                        {viewingTool.fillerEnabled !== false ? (
                          <span className="text-green-600 flex items-center gap-1"><Zap className="w-3 h-3" /> Aktiviert</span>
                        ) : (
                          <span className="text-gray-500">Deaktiviert</span>
                        )}
                      </div>
                      {viewingTool.baseUrl && (
                        <div><strong>Base URL:</strong> <span className="text-blue-600">{viewingTool.baseUrl}</span></div>
                      )}
                      <div><strong>Used:</strong> {viewingTool.usageCount ?? 0} times</div>
                    </div>
                  </div>
                </div>
                <div className="space-y-4">
                  <div>
                    <h4 className="text-sm font-medium text-gray-700 mb-2">Description</h4>
                    <p className="text-sm text-gray-600">{viewingTool.description}</p>
                  </div>
                  <div>
                    <h4 className="text-sm font-medium text-gray-700 mb-2">Tags</h4>
                    <div className="flex flex-wrap gap-1">
                      {(viewingTool.tags || []).filter(tag => tag && typeof tag === 'string').map((tag) => (
                        <Badge key={tag} variant="secondary" className="text-xs">
                          {tag}
                        </Badge>
                      ))}
                    </div>
                  </div>
                  {viewingTool.iconUrl && (
                    <div>
                      <h4 className="text-sm font-medium text-gray-700 mb-2">Icon</h4>
                      <img src={viewingTool.iconUrl} alt="Tool Icon" className="w-12 h-12" />
                    </div>
                  )}
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Schema</h4>
                  <div className="bg-gray-50 rounded-lg p-4 max-h-32 overflow-y-auto overflow-x-hidden">
                    <pre className="text-xs text-gray-800 whitespace-pre-wrap break-words">{JSON.stringify(viewingTool.schema, null, 2)}</pre>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <h4 className="text-sm font-medium text-gray-700 mb-2">Configuration</h4>
                    <div className="bg-gray-50 rounded-lg p-4 max-h-32 overflow-y-auto overflow-x-hidden">
                      <pre className="text-xs text-gray-800 whitespace-pre-wrap break-words">{JSON.stringify(viewingTool.configuration, null, 2)}</pre>
                    </div>
                  </div>
                  <div>
                    <h4 className="text-sm font-medium text-gray-700 mb-2">Execution Config</h4>
                    <div className="bg-gray-50 rounded-lg p-4 max-h-32 overflow-y-auto overflow-x-hidden">
                      <pre className="text-xs text-gray-800 whitespace-pre-wrap break-words">{JSON.stringify(viewingTool.executionConfig, null, 2)}</pre>
                    </div>
                  </div>
                </div>

                <div>
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Documentation</h4>
                  <div className="bg-gray-50 rounded-lg p-4 max-h-48 overflow-y-auto">
                    <div className="prose prose-sm max-w-none">
                      <pre className="whitespace-pre-wrap text-sm text-gray-800">{viewingTool.documentation}</pre>
                    </div>
                  </div>
                </div>

                {/* Functions Section - show if tool uses functions */}
                {viewingTool.usesFunctions && (
                  <div>
                    <h4 className="text-sm font-medium text-gray-700 mb-2">
                      Functions {viewingTool.functions ? `(${viewingTool.functions.length})` : '(0)'}
                    </h4>
                    {viewingTool.functions && viewingTool.functions.length > 0 ? (
                    <div className="space-y-3">
                      {viewingTool.functions.map((func) => (
                        <div key={func.id} className="border rounded-lg p-4 bg-gray-50">
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <span className="font-medium text-sm">{func.displayName}</span>
                              <Badge variant="secondary" className="text-xs">
                                {func.name}
                              </Badge>
                              {func.isActive ? (
                                <Badge className="bg-green-100 text-green-800 text-xs">Active</Badge>
                              ) : (
                                <Badge className="bg-gray-100 text-gray-800 text-xs">Inactive</Badge>
                              )}
                            </div>
                          </div>
                          
                          <p className="text-xs text-gray-600 mb-2">{func.description}</p>
                          
                          <div className="mt-2">
                            <h5 className="text-xs font-medium text-gray-700 mb-1">Schema</h5>
                            <div className="bg-white rounded p-2 max-h-24 overflow-y-auto overflow-x-hidden">
                              <pre className="text-xs text-gray-800 whitespace-pre-wrap break-words">
                                {JSON.stringify(func.schema, null, 2)}
                              </pre>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                    ) : (
                      <div className="text-sm text-gray-500 bg-gray-50 rounded-lg p-4">
                        No functions found. Functions may still be loading or there was an issue extracting them from the schema.
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Authentication Section - only show if auth endpoints are available */}
              {authTypes.length > 0 && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h4 className="text-sm font-medium text-gray-700">
                      <Shield className="w-4 h-4 inline mr-1" />
                      Authentication
                      {viewingTool.authStrategy === 'none' && (
                        <Badge className="ml-2 bg-gray-100 text-gray-600">No Auth</Badge>
                      )}
                      {viewingTool.authStrategy === 'customer' && (
                        <Badge className="ml-2 bg-amber-100 text-amber-800">Customer-managed</Badge>
                      )}
                    </h4>
                    {viewingTool.authStrategy === 'admin' && (
                      <Button variant="outline" size="sm" onClick={handleAddAuthConfig}>
                        <Plus className="w-4 h-4 mr-1" />
                        Add API Key
                      </Button>
                    )}
                  </div>

                  {viewingTool.authStrategy === 'none' && (
                    <div className="text-center py-4 bg-gray-50 border border-gray-200 rounded-lg">
                      <Shield className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                      <p className="text-sm font-medium text-gray-600">No authentication required</p>
                      <p className="text-xs text-gray-500 mt-1">This tool does not require any API keys or credentials.</p>
                    </div>
                  )}

                  {viewingTool.authStrategy === 'customer' && (
                    <div className="text-center py-4 bg-amber-50 border border-amber-200 rounded-lg">
                      <Shield className="w-8 h-8 text-amber-500 mx-auto mb-2" />
                      <p className="text-sm font-medium text-amber-800">Customer provides own API keys</p>
                      <p className="text-xs text-amber-600 mt-1">Customers configure their API keys in the customer portal.</p>
                    </div>
                  )}
                
                {getToolAuthConfigs(viewingTool.id).length > 0 ? (
                  <div className="space-y-2">
                    {getToolAuthConfigs(viewingTool.id).map((config) => (
                      <div key={config.id} className="border rounded-lg p-3 bg-gray-50">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <Badge className={getAuthTypeBadgeColor(config.authType)}>
                              {getAuthTypeDisplayName(config.authType)}
                            </Badge>
                            <span className="text-sm font-medium">{config.headerName}</span>
                            {config.isActive ? (
                              <Badge className="bg-green-100 text-green-800">Active</Badge>
                            ) : (
                              <Badge className="bg-gray-100 text-gray-600">Inactive</Badge>
                            )}
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteAuthConfig(config.id)}
                            className="text-red-600 hover:text-red-700"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                        {config.description && (
                          <p className="text-xs text-gray-600 mt-1">{config.description}</p>
                        )}
                        <div className="text-xs text-gray-500 mt-1">
                          Scope: {config.customerId ? 'Customer' : config.actorId ? 'User' : 'Global'}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-4 bg-gray-50 rounded-lg">
                    <Shield className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                    <p className="text-sm text-gray-500">No authentication configurations</p>
                  </div>
                )}
                </div>
              )}

              <div className="flex justify-end space-x-2">
                <Button variant="outline" onClick={closeDialog}>
                  Close
                </Button>
                <Button onClick={() => handleEdit(viewingTool)}>
                  <Edit className="w-4 h-4 mr-2" />
                  Edit
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Name (technical)</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    placeholder="e.g. weather_api"
                    disabled={!!editingTool}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="displayName">Display Name</Label>
                  <Input
                    id="displayName"
                    value={formData.displayName}
                    onChange={(e) => setFormData({...formData, displayName: e.target.value})}
                    placeholder="e.g. Weather API"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="version">Version</Label>
                  <Input
                    id="version"
                    value={formData.version}
                    onChange={(e) => setFormData({...formData, version: e.target.value})}
                    placeholder="1.0.0"
                    disabled={!!editingTool}
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="category">Category</Label>
                  <Select value={formData.category} onValueChange={(value: any) => setFormData({...formData, category: value})}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="function">Function</SelectItem>
                      <SelectItem value="api">API</SelectItem>
                      <SelectItem value="webhook">Webhook</SelectItem>
                      <SelectItem value="mcp_server">MCP Server</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="author">Author</Label>
                  <Input
                    id="author"
                    value={formData.author}
                    onChange={(e) => setFormData({...formData, author: e.target.value})}
                    placeholder="Your name or team"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="authStrategy">Authentication Strategy</Label>
                <Select
                  value={formData.authStrategy ?? 'admin'}
                  onValueChange={(value: 'none' | 'admin' | 'customer') => setFormData({...formData, authStrategy: value})}
                >
                  <SelectTrigger id="authStrategy">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No Authentication</SelectItem>
                    <SelectItem value="admin">Admin-managed (Platform provides API keys)</SelectItem>
                    <SelectItem value="customer">Customer-managed (Customer provides own API keys)</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-gray-500">
                  {(formData.authStrategy ?? 'admin') === 'none'
                    ? 'This tool requires no authentication.'
                    : (formData.authStrategy ?? 'admin') === 'customer'
                    ? 'Customers must configure their own API keys in the customer portal.'
                    : 'Platform administrator manages API keys for all customers.'}
                </p>
              </div>

              {/* Customer Auth Template - only shown when authStrategy = customer */}
              {(formData.authStrategy ?? 'admin') === 'customer' && (
                <div className="space-y-3 border rounded-lg p-4 bg-amber-50">
                  <h4 className="text-sm font-medium text-amber-800">Customer Auth Template</h4>
                  <p className="text-xs text-amber-600">Define what the customer needs to provide. They will only see a single input field.</p>

                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <Label htmlFor="customerAuthType">Auth Type</Label>
                      <Select
                        value={formData.customerAuthType || 'bearer'}
                        onValueChange={(value) => {
                          const headerName = value === 'bearer' ? 'Authorization'
                            : value === 'api_key' ? 'X-API-Key'
                            : '';
                          setFormData({...formData, customerAuthType: value, customerAuthHeaderName: headerName});
                        }}
                      >
                        <SelectTrigger id="customerAuthType">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="bearer">Bearer Token</SelectItem>
                          <SelectItem value="api_key">API Key</SelectItem>
                          <SelectItem value="header">Custom Header</SelectItem>
                          <SelectItem value="basic_auth">Basic Auth</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1">
                      <Label htmlFor="customerAuthHeaderName">Header Name</Label>
                      <Input
                        id="customerAuthHeaderName"
                        value={formData.customerAuthHeaderName || ''}
                        onChange={(e) => setFormData({...formData, customerAuthHeaderName: e.target.value})}
                        placeholder="e.g. Authorization, X-API-Key"
                      />
                    </div>
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="customerAuthDescription">Description for Customer</Label>
                    <Input
                      id="customerAuthDescription"
                      value={formData.customerAuthDescription || ''}
                      onChange={(e) => setFormData({...formData, customerAuthDescription: e.target.value})}
                      placeholder="e.g. Enter your Dify API Key"
                    />
                    <p className="text-xs text-gray-500">This text is shown to the customer in the portal as a hint.</p>
                  </div>
                </div>
              )}

              {/* Base URL field - only shown for API tools */}
              {(formData.category === 'api' || formData.category === 'webhook') && (
                <div className="space-y-2">
                  <Label htmlFor="baseUrl">Base URL</Label>
                  <Input
                    id="baseUrl"
                    value={formData.baseUrl || ''}
                    onChange={(e) => setFormData({...formData, baseUrl: e.target.value})}
                    placeholder="https://api.example.com"
                  />
                  <p className="text-xs text-gray-500">
                    The base URL for API requests. Function paths will be appended to this URL.
                  </p>
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  placeholder="What does this tool do?"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="tags">Tags (comma separated)</Label>
                  <Input
                    id="tags"
                    value={tagsInput}
                    onChange={(e) => setTagsInput(e.target.value)}
                    onBlur={() => {
                      const tags = tagsInput
                        .split(',')
                        .map(tag => tag.trim())
                        .filter(tag => tag.length > 0);
                      setFormData({...formData, tags});
                    }}
                    placeholder="api, weather, location"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="iconUrl">Icon URL</Label>
                  <Input
                    id="iconUrl"
                    value={formData.iconUrl}
                    onChange={(e) => setFormData({...formData, iconUrl: e.target.value})}
                    placeholder="https://api.iconify.design/..."
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="schema">Schema (JSON or YAML)</Label>
                <div className="flex gap-2 mb-2">
                  <Input
                    placeholder="Load schema from URL (e.g., https://api.example.com/openapi.yaml)"
                    value={schemaUrl}
                    onChange={(e) => setSchemaUrl(e.target.value)}
                    className="flex-1"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={loadSchemaFromUrl}
                    disabled={!schemaUrl.trim() || loadingSchema}
                    className="shrink-0"
                  >
                    {loadingSchema ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                        Loading
                      </>
                    ) : (
                      <>
                        <Download className="w-4 h-4 mr-1" />
                        Load from URL
                      </>
                    )}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => schemaFileInputRef.current?.click()}
                    className="shrink-0"
                  >
                    <Upload className="w-4 h-4 mr-1" />
                    Upload File
                  </Button>
                  <input
                    ref={schemaFileInputRef}
                    type="file"
                    accept=".json,.yaml,.yml,application/json,application/x-yaml,text/yaml"
                    className="hidden"
                    onChange={(e) => { void handleSchemaFileUpload(e); }}
                  />
                </div>
                <Textarea
                  id="schema"
                  value={JSON.stringify(formData.schema, null, 2)}
                  onChange={(e) => {
                    try {
                      applyLoadedSchema(parseSchemaText(e.target.value));
                    } catch {
                      // Incomplete/invalid JSON or YAML while typing — ignore until valid.
                    }
                  }}
                  placeholder={'Paste an OpenAPI/Swagger spec (JSON or YAML) or upload a file above'}
                  className="min-h-24 !max-h-96 font-mono text-xs !resize-y !overflow-y-auto whitespace-pre-wrap break-words"
                  style={{ maxHeight: '384px', resize: 'vertical', overflowY: 'auto', overflowX: 'hidden', wordBreak: 'break-all' }}
                />
                <p className="text-xs text-gray-500">
                  Accepts JSON or YAML. For OpenAPI/Swagger specs the Base URL is filled automatically from the schema when left empty.
                </p>
              </div>

              <Collapsible open={advancedOpen} onOpenChange={setAdvancedOpen}>
                <CollapsibleTrigger asChild>
                  <Button type="button" variant="ghost" className="px-0 hover:bg-transparent">
                    <ChevronDown className={`w-4 h-4 mr-1 transition-transform ${advancedOpen ? 'rotate-180' : ''}`} />
                    Advanced settings (optional)
                  </Button>
                </CollapsibleTrigger>
                <CollapsibleContent className="space-y-4 pt-2">
                  <p className="text-xs text-gray-500">
                    Low-level overrides — for OpenAPI/Swagger tools you normally leave these empty.
                    Authentication and request headers are configured separately in the Authentication section, not here.
                  </p>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="configuration">Configuration (JSON)</Label>
                      <Textarea
                        id="configuration"
                        value={JSON.stringify(formData.configuration, null, 2)}
                        onChange={(e) => {
                          try {
                            const parsed = JSON.parse(e.target.value);
                            setFormData({...formData, configuration: parsed});
                          } catch {
                            // Invalid JSON, ignore for now
                          }
                        }}
                        placeholder='{"endpoint": "https://api.example.com", "method": "POST"}'
                        className="min-h-24 !max-h-64 font-mono text-xs !resize-y !overflow-y-auto whitespace-pre-wrap break-words"
                        style={{ maxHeight: '256px', resize: 'vertical', overflowY: 'auto', overflowX: 'hidden', wordBreak: 'break-all' }}
                      />
                      <p className="text-xs text-gray-500">
                        Runtime config for simple (non-OpenAPI) API tools (endpoint/method) and MCP tools. OpenAPI tools derive this from the schema.
                      </p>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="executionConfig">Execution Config (JSON)</Label>
                      <Textarea
                        id="executionConfig"
                        value={JSON.stringify(formData.executionConfig, null, 2)}
                        onChange={(e) => {
                          try {
                            const parsed = JSON.parse(e.target.value);
                            setFormData({...formData, executionConfig: parsed});
                          } catch {
                            // Invalid JSON, ignore for now
                          }
                        }}
                        placeholder='{"timeout": 30000}'
                        className="min-h-24 !max-h-64 font-mono text-xs !resize-y !overflow-y-auto whitespace-pre-wrap break-words"
                        style={{ maxHeight: '256px', resize: 'vertical', overflowY: 'auto', overflowX: 'hidden', wordBreak: 'break-all' }}
                      />
                      <p className="text-xs text-gray-500">
                        Execution options. Currently supports <code>timeout</code> in milliseconds (default 30000).
                      </p>
                    </div>
                  </div>
                </CollapsibleContent>
              </Collapsible>

              <div className="space-y-2">
                <Label htmlFor="documentation">Documentation (Markdown)</Label>
                <Textarea
                  id="documentation"
                  value={formData.documentation}
                  onChange={(e) => setFormData({...formData, documentation: e.target.value})}
                  placeholder="# Tool Name&#10;&#10;Description of the tool...&#10;&#10;## Parameters&#10;..."
                  className="min-h-32 !max-h-64 !resize-y !overflow-y-auto"
                  style={{ maxHeight: '256px', resize: 'vertical', overflowY: 'auto' }}
                />
              </div>

              <div className="flex items-center space-x-6">
                <div className="flex items-center space-x-2">
                  <Switch
                    id="isPublic"
                    checked={formData.isPublic}
                    onCheckedChange={(checked) => setFormData({...formData, isPublic: checked})}
                  />
                  <Label htmlFor="isPublic">Publicly available</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Switch
                    id="fillerEnabled"
                    checked={formData.fillerEnabled ?? true}
                    onCheckedChange={(checked) => setFormData({...formData, fillerEnabled: checked})}
                  />
                  <Label htmlFor="fillerEnabled" className="flex items-center gap-1">
                    <Zap className="w-4 h-4 text-yellow-500" />
                    Filler aktiviert
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Switch
                    id="detectDynamicBodyFields"
                    checked={formData.detectDynamicBodyFields ?? false}
                    onCheckedChange={(checked) => setFormData({...formData, detectDynamicBodyFields: checked})}
                  />
                  <Label htmlFor="detectDynamicBodyFields">Spec-Beispiele auswerten</Label>
                </div>
              </div>
              <p className="text-xs text-gray-500">
                Spec-Beispiele auswerten: fuer APIs, die Daten unter einer ID statt unter festen
                Feldnamen erwarten. Erlaubt dem Modell zusaetzliche Body-Felder und wirkt erst,
                wenn die Funktionen des Tools neu extrahiert werden.
              </p>

              {/* Authentication Section */}
              {editingTool && (
                <div className="space-y-4 border-t pt-4">
                  <div className="flex items-center justify-between">
                    <h4 className="text-sm font-medium text-gray-700">
                      <Shield className="w-4 h-4 inline mr-1" />
                      Authentication
                    </h4>
                    {(formData.authStrategy ?? 'admin') === 'admin' && authTypes.length > 0 && (
                      <Button variant="outline" size="sm" onClick={handleAddAuthConfig}>
                        <Plus className="w-4 h-4 mr-1" />
                        Add API Key
                      </Button>
                    )}
                  </div>

                  {(formData.authStrategy ?? 'admin') === 'none' && (
                    <div className="text-center py-4 bg-gray-50 border border-gray-200 rounded-lg">
                      <Shield className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                      <p className="text-sm font-medium text-gray-600">No authentication required</p>
                      <p className="text-xs text-gray-500 mt-1">This tool does not require any API keys or credentials.</p>
                    </div>
                  )}

                  {(formData.authStrategy ?? 'admin') === 'customer' && (
                    <div className="text-center py-4 bg-amber-50 border border-amber-200 rounded-lg">
                      <Shield className="w-8 h-8 text-amber-500 mx-auto mb-2" />
                      <p className="text-sm font-medium text-amber-800">Customer provides own API keys</p>
                      <p className="text-xs text-amber-600 mt-1">Customers must configure their API keys in the customer portal.</p>
                    </div>
                  )}

                  {getToolAuthConfigs(editingTool.id).length > 0 ? (
                    <div className="space-y-2">
                      {getToolAuthConfigs(editingTool.id).map((config) => (
                        <div key={config.id} className="border rounded-lg p-3 bg-gray-50">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <Badge className={getAuthTypeBadgeColor(config.authType)}>
                                {getAuthTypeDisplayName(config.authType)}
                              </Badge>
                              <span className="text-sm font-medium">{config.headerName}</span>
                              {config.isActive ? (
                                <Badge className="bg-green-100 text-green-800">Active</Badge>
                              ) : (
                                <Badge className="bg-gray-100 text-gray-600">Inactive</Badge>
                              )}
                            </div>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteAuthConfig(config.id)}
                              className="text-red-600 hover:text-red-700"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                          {config.description && (
                            <p className="text-xs text-gray-600 mt-1">{config.description}</p>
                          )}
                          <div className="text-xs text-gray-500 mt-1">
                            Scope: {config.customerId ? 'Customer' : config.actorId ? 'User' : 'Global'} • 
                            Created: {new Date(config.createdAt).toLocaleDateString()}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-4 bg-gray-50 rounded-lg">
                      <Shield className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                      <p className="text-sm text-gray-500">No authentication configurations</p>
                    </div>
                  )}
                  
                  {/* Authentication Form */}
                  {showAuthSection && (formData.authStrategy ?? 'admin') === 'admin' && (
                    <div className="border rounded-lg p-4 bg-blue-50">
                      <h5 className="text-sm font-medium text-gray-900 mb-3">Add Authentication Configuration</h5>
                      <div className="space-y-3">
                        <div className="grid grid-cols-2 gap-3">
                          <div className="space-y-1">
                            <Label htmlFor="authType">Authentication Type</Label>
                            <Select 
                              value={authFormData.authType} 
                              onValueChange={(value: AuthType) => setAuthFormData({...authFormData, authType: value})}
                            >
                              <SelectTrigger>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {authTypes.map((type) => (
                                  <SelectItem key={type.id} value={type.id}>
                                    {type.name}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="space-y-1">
                            <Label htmlFor="headerName">Header Name</Label>
                            <Input
                              value={authFormData.headerName}
                              onChange={(e) => setAuthFormData({...authFormData, headerName: e.target.value})}
                              placeholder="Authorization, X-API-Key, etc."
                            />
                          </div>
                        </div>
                        
                        <div className="space-y-1">
                          <Label htmlFor="headerValue">Header Value</Label>
                          <Input
                            type="password"
                            value={authFormData.headerValue}
                            onChange={(e) => setAuthFormData({...authFormData, headerValue: e.target.value})}
                            placeholder="Bearer token, API key, etc."
                          />
                        </div>

                        <div className="space-y-1">
                          <Label htmlFor="description">Description</Label>
                          <Input
                            value={authFormData.description}
                            onChange={(e) => setAuthFormData({...authFormData, description: e.target.value})}
                            placeholder="Optional description"
                          />
                        </div>

                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-2">
                            <Switch
                              checked={authFormData.isActive}
                              onCheckedChange={(checked) => setAuthFormData({...authFormData, isActive: checked})}
                            />
                            <Label>Active</Label>
                          </div>
                          
                          <div className="flex gap-2">
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => setShowAuthSection(false)}
                            >
                              Cancel
                            </Button>
                            <Button 
                              size="sm"
                              onClick={handleSaveAuthConfig}
                              disabled={savingAuth || !authFormData.headerName || !authFormData.headerValue}
                            >
                              {savingAuth ? (
                                <>
                                  <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                                  Saving...
                                </>
                              ) : (
                                'Add Configuration'
                              )}
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              <div className="flex justify-end space-x-2">
                <Button variant="outline" onClick={closeDialog} disabled={saving}>
                  Cancel
                </Button>
                <Button onClick={handleSave} disabled={saving}>
                  {saving ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      {editingTool ? 'Saving...' : 'Creating...'}
                    </>
                  ) : (
                    editingTool ? 'Save' : 'Create'
                  )}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}